import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnActions } from 'src/app/components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import { Regulator } from 'src/app/models/entities/regulator';
import { withDeleteFeedback } from '../regulators-ui.utils';

@Component({
  selector: 'app-regulators-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './regulators-index-page.component.html',
  styleUrl: './regulators-index-page.component.scss',
})
export class RegulatorsIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;

  /** Adapter used by the data-table delete button (adds toasts and error handling). */
  readonly deleteService = {
    delete: (regulatorId: number) =>
      withDeleteFeedback(this.regulatorsService.delete(regulatorId), this.alertNotificationService, 'Regulator')
  };

  private subscriptions = new Subscription();

  constructor(
    protected regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Regulators',
      cardTitle: 'Regulators',
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          code: null,
          name: null,
        },
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            width: '200px',
            value: (item: Regulator) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'name',
            header: 'Name',
            filter: 'text',
            value: (item: Regulator) => item?.name ?? ''
          }),
          new DataTableColumnText({
            attribute: 'externalCode',
            header: 'External Code',
            width: '180px',
            value: (item: Regulator) => item?.externalCode || '-'
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            sortable: false,
            cellClass: 'text-center',
            style: { width: '200px' },
            buttons: [
              {
                icon: 'fa-users',
                tooltip: 'Clients',
                aclAction: 'view',
                onClick: (item: Regulator) => this.routeService.regulatorClients(item.regulatorId)
              },
              {
                icon: 'fa-desktop',
                tooltip: 'VLT',
                aclAction: 'view',
                onClick: (item: Regulator) => this.routeService.regulatorVlts(item.regulatorId)
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit regulator',
                aclAction: 'modify',
                onClick: (item: Regulator) => this.routeService.regulatorUpdate(item.regulatorId)
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete regulator',
                aclAction: 'delete',
                isDelete: true
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.regulatorsService.list(options),
      responseListModelName: 'regulators',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        const canCreate = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
        this.baseIndexPageConfig.addNewItemButton = canCreate
          ? {
              type: 'success',
              title: 'Create Regulator',
              onClick: () => this.routeService.regulatorCreate(),
            }
          : undefined;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
